import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Console i18n — deliberately tiny.
 *
 * The platform's own i18n capability lives in platform-core
 * (`GET /api/v1/platform/i18n/messages`), which serves *catalog* copy and FX
 * rates. Chrome copy for the operator console is bundled so the shell renders
 * instantly, offline and before any token exists.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
const DICT = {
    "zh-CN": {
        "app.name": "VNMade 平台",
        "app.tagline": "AI 原生的全球制造与贸易平台",
        "nav.commerce": "商业系统",
        "nav.rnd": "AI 产品研发",
        "nav.scm": "供应链控制塔",
        "nav.mfg": "制造网络",
        "nav.admin": "平台管理",
        "nav.infrastructure": "控制台",
        "action.refresh": "刷新",
        "action.reload": "重新加载",
        "action.retry": "重试",
        "action.create": "新建",
        "action.submit": "提交",
        "action.submitting": "提交中…",
        "action.cancel": "取消",
        "action.save": "保存",
        "action.close": "关闭",
        "action.signOut": "退出",
        "action.open": "打开",
        "action.copy": "复制",
        "action.copied": "已复制",
        "state.loading": "加载中…",
        "state.empty": "暂无数据",
        "state.error": "请求失败",
        "state.notImplemented": "后端尚未提供该能力",
        "label.tenant": "租户",
        "label.role": "角色",
        "label.priority": "优先级",
        "label.owner": "数据归属",
        "label.api": "依赖接口",
        "label.responsibility": "页面职责",
        "label.search": "搜索商品、订单、RFQ…",
        "label.filter": "筛选",
        "label.all": "全部",
        "label.status": "状态",
        "label.amount": "金额",
        "label.created": "创建时间",
        "label.currency": "币种",
        "toast.saved": "已保存",
        "toast.submitted": "已提交",
    },
    en: {
        "app.name": "VNMade Platform",
        "app.tagline": "AI-native global manufacturing & commerce",
        "nav.commerce": "Commerce",
        "nav.rnd": "AI Product R&D",
        "nav.scm": "Supply Chain Control Tower",
        "nav.mfg": "Manufacturing Network",
        "nav.admin": "Platform Admin",
        "nav.infrastructure": "Console",
        "action.refresh": "Refresh",
        "action.reload": "Reload",
        "action.retry": "Retry",
        "action.create": "New",
        "action.submit": "Submit",
        "action.submitting": "Submitting…",
        "action.cancel": "Cancel",
        "action.save": "Save",
        "action.close": "Close",
        "action.signOut": "Sign out",
        "action.open": "Open",
        "action.copy": "Copy",
        "action.copied": "Copied",
        "state.loading": "Loading…",
        "state.empty": "Nothing here yet",
        "state.error": "Request failed",
        "state.notImplemented": "This capability has no backend yet",
        "label.tenant": "Tenant",
        "label.role": "Role",
        "label.priority": "Priority",
        "label.owner": "Data owner",
        "label.api": "Endpoints",
        "label.responsibility": "Responsibility",
        "label.search": "Search products, orders, RFQs…",
        "label.filter": "Filter",
        "label.all": "All",
        "label.status": "Status",
        "label.amount": "Amount",
        "label.created": "Created",
        "label.currency": "Currency",
        "toast.saved": "Saved",
        "toast.submitted": "Submitted",
    },
};
const Ctx = createContext(null);
const KEY = "vnmade.locale.v1";
export function I18nProvider({ children }) {
    const [locale, setLocaleState] = useState(() => {
        const stored = localStorage.getItem(KEY);
        return stored === "en" ? "en" : "zh-CN";
    });
    useEffect(() => {
        localStorage.setItem(KEY, locale);
        document.documentElement.lang = locale;
    }, [locale]);
    const setLocale = useCallback((l) => setLocaleState(l), []);
    const t = useCallback((key, fallback) => DICT[locale][key] ?? DICT["zh-CN"][key] ?? fallback ?? key, [locale]);
    const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
    return _jsx(Ctx.Provider, { value: value, children: children });
}
export function useI18n() {
    const v = useContext(Ctx);
    if (!v)
        throw new Error("useI18n must be used inside <I18nProvider>");
    return v;
}
